import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  extractGoldenStandardText,
  createGoldenStandard,
  updateGoldenStandard,
  getGoldenStandard,
  checkGoldenStandardDuplicates,
} from "../services/api";
import { useAllowedCategories, useCategories } from "../hooks/useCategories";
import { CategorySelectDialog } from "../components/CategorySelectDialog";
import { ConfirmDialog } from "../components/common/ConfirmDialog";
import { DuplicateMatchesList } from "../components/common/DuplicateMatchesList";
import toast from "react-hot-toast";
import { isModelUnavailableError } from "../utils/llmAvailability";
import { GoldenStandardCreateRequest, DuplicateMatch } from "../types";
import { computeFileSha256 } from "../utils/fileHash";

export const GoldenStandardPage: React.FC = () => {
  const navigate = useNavigate();
  const { chunkId } = useParams<{ chunkId: string }>();
  const isEditMode = Boolean(chunkId);
  const { data: categories = [] } = useAllowedCategories();
  // Elenco completo (non filtrato), usato solo per capire se la categoria
  // rilevata dall'estrazione automatica è reale ma non consentita per l'utente.
  const { data: allCategories = [] } = useCategories();
  const [categoryUnauthorizedLabel, setCategoryUnauthorizedLabel] =
    React.useState<string | null>(null);
  const [title, setTitle] = React.useState("");
  const [publicationDate, setPublicationDate] = React.useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  });
  const [categoryValue, setCategoryValue] = React.useState("altro");
  const [text, setText] = React.useState("");
  const [extracting, setExtracting] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [checkingDuplicates, setCheckingDuplicates] = React.useState(false);
  const [loadingEdit, setLoadingEdit] = React.useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = React.useState(false);
  const [sourceFileHash, setSourceFileHash] = React.useState<string | null>(null);
  const [duplicateMatches, setDuplicateMatches] = React.useState<DuplicateMatch[] | null>(
    null
  );
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (!categoryValue) {
      setCategoryValue("altro");
    }
  }, [categories, categoryValue]);

  // Carica Golden Standard esistente in modifica
  React.useEffect(() => {
    if (!chunkId) return;
    const id = Number(chunkId);
    if (Number.isNaN(id)) return;
    setLoadingEdit(true);
    getGoldenStandard(id)
      .then((item) => {
        setTitle(item.title ?? "");
        setText(item.text ?? "");
        setCategoryValue(item.category ?? "");
        if (item.date) setPublicationDate(item.date.slice(0, 10));
        else if (item.created_at)
          setPublicationDate(item.created_at.slice(0, 10));
      })
      .catch((err: { response?: { status?: number } }) => {
        if (err?.response?.status === 404) {
          toast.error("Golden Standard non trovato");
        } else {
          toast.error("Errore caricamento Golden Standard");
        }
      })
      .finally(() => setLoadingEdit(false));
  }, [chunkId]);

  const handleFileSelect = async (file: File) => {
    setExtracting(true);
    try {
      const [result, hash] = await Promise.all([
        extractGoldenStandardText(file),
        computeFileSha256(file).catch(() => null),
      ]);
      setSourceFileHash(hash);
      setTitle(result.title || "");
      setPublicationDate(result.date || publicationDate);
      setText(result.text || "");

      const normalized = (result.category || "").trim().toLowerCase();
      const matchedAllowed = normalized
        ? categories.find((c) => c.value.trim().toLowerCase() === normalized)
        : undefined;
      if (matchedAllowed) {
        setCategoryValue(matchedAllowed.value);
        setCategoryUnauthorizedLabel(null);
      } else {
        const matchedAny = normalized
          ? allCategories.find((c) => c.value.trim().toLowerCase() === normalized)
          : undefined;
        if (matchedAny) {
          // Categoria reale ma non consentita per l'utente corrente: non
          // precompilare, richiedi selezione manuale tra quelle permesse.
          setCategoryUnauthorizedLabel(matchedAny.label || matchedAny.value);
        } else if (result.category) {
          setCategoryValue(result.category);
        }
      }
      toast.success("Testo estratto dal documento");
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Errore estrazione testo";
      console.error("[GoldenStandard] extract text failed:", {
        file: file?.name,
        err,
      });
      if (!isModelUnavailableError(err)) {
        toast.error(message);
      }
    } finally {
      setExtracting(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (
      file &&
      (file.type === "application/pdf" || file.type.startsWith("text/"))
    ) {
      handleFileSelect(file);
    } else {
      toast.error("Inserisci un file PDF o di testo");
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    e.target.value = "";
  };

  const doCreateOrUpdate = async () => {
    setSubmitting(true);
    try {
      if (isEditMode && chunkId) {
        const id = Number(chunkId);
        if (Number.isNaN(id)) throw new Error("ID non valido");
        await updateGoldenStandard(id, {
          title: title.trim() || undefined,
          text: text.trim(),
          category: categoryValue || undefined,
          date: publicationDate || undefined,
        });
        toast.success("Golden Standard aggiornato");
      } else {
        const request: GoldenStandardCreateRequest = {
          title: title.trim() || "Golden Standard",
          text: text.trim(),
          category: categoryValue,
          date: publicationDate || undefined,
          source_file_hash: sourceFileHash || undefined,
        };
        await createGoldenStandard(request);
        toast.success("Golden Standard creata");
        setText("");
        setTitle("");
        setSourceFileHash(null);
      }
      navigate("/knowledge-base");
    } catch (err: unknown) {
      toast.error(
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ??
          (isEditMode
            ? "Errore aggiornamento Golden Standard"
            : "Errore creazione Golden Standard")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (!text.trim()) {
      toast.error("Inserisci il testo della Golden Standard");
      return;
    }
    // Il controllo duplicati si applica solo alla creazione: in modifica il record esistente
    // corrisponderebbe sempre a se stesso.
    if (!isEditMode) {
      setCheckingDuplicates(true);
      try {
        const { matches } = await checkGoldenStandardDuplicates({
          title: title.trim() || "Golden Standard",
          text: text.trim(),
          category: categoryValue,
          date: publicationDate || undefined,
          source_file_hash: sourceFileHash || undefined,
        });
        if (matches.length > 0) {
          setDuplicateMatches(matches);
          return;
        }
      } catch (err) {
        console.error("[GoldenStandard] check-duplicates failed:", err);
      } finally {
        setCheckingDuplicates(false);
      }
    }
    await doCreateOrUpdate();
  };

  const categoryLabel =
    categories.find((c) => c.value === categoryValue)?.label ??
    (categoryValue === "altro" ? "Altro" : "Seleziona categoria");

  if (loadingEdit) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "#F5F5F5",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography variant="h5" fontWeight={600}>
        {isEditMode ? "Modifica Golden Standard" : "Nuovo Golden Standard"}
      </Typography>

      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-end",
          }}
        >
          <TextField
            size="small"
            label="Titolo"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Titolo"
            sx={{ flex: "1 1 200px", minWidth: 160 }}
          />
          <DatePicker
            label="Data pubblicazione"
            value={publicationDate ? dayjs(publicationDate) : null}
            onChange={(newValue) =>
              setPublicationDate(
                newValue ? newValue.format("YYYY-MM-DD") : ""
              )
            }
            slotProps={{
              textField: {
                size: "small",
                sx: { width: 200 },
              },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            color="inherit"
            onClick={() => setCategoryDialogOpen(true)}
            endIcon={<ExpandMoreIcon sx={{ fontSize: 20, flexShrink: 0 }} />}
            sx={{
              justifyContent: "space-between",
              textTransform: "none",
              height: 40,
              minWidth: 200,
              px: 1.5,
              py: 0,
              border: "1px solid",
              borderColor: "#cacaca",
              bgcolor: "background.paper",
              borderRadius: 1,
              "&:hover": { borderColor: "black" },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                flex: 1,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "left",
                color: "text.primary",
              }}
            >
              {categoryLabel}
            </Typography>
          </Button>
        </Box>
        {categoryUnauthorizedLabel && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {`Non sei autorizzato a creare Golden Standard di categoria "${categoryUnauthorizedLabel}"; la classificazione automatica ha rilevato questa categoria ma dovrai selezionarne una tra quelle consentite, assumendotene la responsabilità.`}
          </Alert>
        )}
      </Paper>

      <Paper sx={{ p: 3, borderRadius: 4 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,application/pdf,text/plain,.txt"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />
        <Box
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 2,
            p: 3,
            textAlign: "center",
            cursor: "pointer",
            mb: 2,
            "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
          }}
        >
          {extracting ? (
            <CircularProgress size={40} sx={{ mb: 1 }} />
          ) : (
            <CloudUploadIcon
              sx={{ fontSize: 48, color: "text.secondary", mb: 1 }}
            />
          )}
          <Typography color="text.secondary">
            {extracting
              ? "Estrazione in corso..."
              : "Trascina un PDF o file di testo qui, oppure clicca per selezionare"}
          </Typography>
        </Box>
        <TextField
          multiline
          minRows={20}
          label="Testo Golden Standard"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Scrivi o incolla il testo qui..."
          fullWidth
          sx={{ "& .MuiInputBase-root": { alignItems: "flex-start" } }}
        />
      </Paper>

      {/* Bottone Avvia caricamento */}
      <Paper
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          borderRadius: 4,
        }}
      >
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          sx={{ width: "100%" }}
          disabled={!text.trim() || submitting || checkingDuplicates}
          startIcon={
            submitting || checkingDuplicates ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <CloudUploadIcon />
            )
          }
        >
          {submitting
            ? "Salvataggio..."
            : checkingDuplicates
              ? "Verifica duplicati in corso..."
              : isEditMode
                ? "Salva modifiche"
                : "Carica Golden Standard"}
        </Button>
      </Paper>

      <ConfirmDialog
        open={!!duplicateMatches}
        title="Possibile Golden Standard duplicata"
        content={
          duplicateMatches && (
            <DuplicateMatchesList fileLabel={title} matches={duplicateMatches} />
          )
        }
        confirmText="Procedi comunque"
        cancelText="Annulla"
        loading={submitting}
        onCancel={() => setDuplicateMatches(null)}
        onConfirm={async () => {
          setDuplicateMatches(null);
          await doCreateOrUpdate();
        }}
      />

      <CategorySelectDialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        value={categoryValue}
        onConfirm={(value) => {
          setCategoryValue(value);
          setCategoryUnauthorizedLabel(null);
          setCategoryDialogOpen(false);
        }}
        title="Seleziona categoria"
        categories={categories}
      />
    </Box>
  );
};
